/**
 * helpSearch — moteur de recherche lexicale LOCAL du Help Drawer (HELP-DRAWER §4).
 *
 * À la frappe, AUCUN aller-retour réseau : tout se filtre/score/trie en mémoire à
 * partir de l'index léger (`slug, title, tags, category, summary`).
 *
 * Repli d'accents **1 caractère → 1 caractère** (é→e, à→a, ç→c…) pour préserver
 * les positions et surligner au bon endroit — surtout PAS `normalize('NFD')`
 * (la décomposition décale les index).
 */

// ── Index item (miroir de HelpSearchIndexItem backend) ───────────────────────

export interface HelpSearchItem {
    slug: string
    title: string
    tags: string[]
    category?: string | null
    /** Réservé au matching, jamais affiché. */
    summary?: string | null
}

/** Item avec champs cherchables pré-normalisés (repliés au chargement). */
export interface FoldedHelpItem extends HelpSearchItem {
    _title: string
    _titleWords: string[]
    /** tags thématiques (hors `page:`/`tab:`…) repliés. */
    _tags: string
    _summary: string
}

export interface HelpSearchResult {
    item: HelpSearchItem
    score: number
    /** L'article est rattaché à la page/onglet courant (badge « Sur cette page »). */
    onThisPage: boolean
}

// ── Repli d'accents 1:1 ──────────────────────────────────────────────────────

const FOLD: Record<string, string> = {}
for (const [base, chars] of [
    ['a', 'àâäáãåæ'],
    ['c', 'ç'],
    ['e', 'èéêëē'],
    ['i', 'ìîïíį'],
    ['n', 'ñ'],
    ['o', 'òôöóõøœ'],
    ['u', 'ùûüú'],
    ['y', 'ÿý'],
] as const) {
    for (const ch of chars) FOLD[ch] = base
    // Variantes majuscules (É→E…), pour préserver la casse hors recherche.
    const upperBase = base.toUpperCase()
    for (const ch of chars.toUpperCase()) FOLD[ch] = upperBase
}

/** Replie les accents 1:1 (préserve la longueur et les positions). */
export function foldAccents(input: string): string {
    let out = ''
    for (const ch of input) out += FOLD[ch] ?? ch
    return out
}

/** Minuscule + repli d'accents (longueur préservée). */
export function normalizeForSearch(input: string): string {
    return foldAccents(input.toLowerCase())
}

/** Un tag est « technique » (scope contextuel) s'il contient `:` (page:, tab:…). */
function isScopeTag(tag: string): boolean {
    return tag.includes(':')
}

/** Pré-normalise un item d'index une fois pour toutes (au chargement). */
export function buildFoldedItems(items: HelpSearchItem[]): FoldedHelpItem[] {
    return items.map((item) => {
        const _title = normalizeForSearch(item.title)
        return {
            ...item,
            _title,
            _titleWords: _title.split(/[^a-z0-9]+/i).filter(Boolean),
            _tags: normalizeForSearch(
                (item.tags ?? []).filter((t) => !isScopeTag(t)).join(' '),
            ),
            _summary: normalizeForSearch(item.summary ?? ''),
        }
    })
}

// ── Scoring ──────────────────────────────────────────────────────────────────

function isSubsequence(needle: string, hay: string): boolean {
    let i = 0
    for (let j = 0; j < hay.length && i < needle.length; j++) {
        if (hay[j] === needle[i]) i++
    }
    return i === needle.length
}

/**
 * Score d'un item pour une requête déjà normalisée (repliée minuscule).
 * Échelle décroissante : préfixe titre > préfixe de mot > sous-chaîne titre >
 * tokens dans le titre > tokens dans tags/summary > sous-séquence. `0` = exclu.
 *
 * Exposé isolément pour les tests (HELP-DRAWER §4.2).
 */
export function scoreItem(normalizedQuery: string, item: FoldedHelpItem): number {
    const nq = normalizedQuery.trim()
    if (!nq) return 0
    const tokens = nq.split(/\s+/).filter(Boolean)
    const noSpace = nq.replace(/\s+/g, '')

    const title = item._title
    const haystack = `${title} ${item._tags} ${item._summary}`

    // Préfixe exact du titre (meilleur signal).
    if (title.startsWith(nq)) return 100
    // Tous les tokens préfixent un mot du titre (ex. "fact cli" → "facturation client").
    if (tokens.every((tk) => item._titleWords.some((w) => w.startsWith(tk)))) return 85
    // Sous-chaîne contiguë du titre (phrase entière).
    if (title.includes(nq)) return 70
    // Tous les tokens présents (en sous-chaîne) dans le titre.
    if (tokens.every((tk) => title.includes(tk))) return 60
    // Tous les tokens présents dans titre + tags + summary.
    if (tokens.every((tk) => haystack.includes(tk))) return 40
    // Tolérance fautes légères : sous-séquence dans le titre.
    if (noSpace.length >= 3 && isSubsequence(noSpace, title)) return 20
    return 0
}

export interface SearchOptions {
    /** Nb max de résultats (annexe : 8). */
    limit?: number
    /** Slugs rattachés à la page/onglet courant (bonus « Sur cette page »). */
    contextSlugs?: Set<string> | string[]
    /** Bonus de score appliqué aux articles « sur cette page » (dégradable). */
    contextBonus?: number
}

/**
 * Filtre + score + trie + limite, en JS pur (pas de debounce, pas de réseau).
 * Liste PLATE triée par score (le regroupement par catégorie ne s'applique
 * qu'en état Contextuel, champ vide — cf. §6).
 */
export function searchArticles(
    query: string,
    items: FoldedHelpItem[],
    { limit = 8, contextSlugs, contextBonus = 8 }: SearchOptions = {},
): HelpSearchResult[] {
    const nq = normalizeForSearch(query.trim())
    if (!nq) return []
    const ctx =
        contextSlugs instanceof Set
            ? contextSlugs
            : new Set(contextSlugs ?? [])

    const scored: HelpSearchResult[] = []
    for (const item of items) {
        const base = scoreItem(nq, item)
        if (base <= 0) continue
        const onThisPage = ctx.has(item.slug)
        scored.push({
            item,
            score: base + (onThisPage ? contextBonus : 0),
            onThisPage,
        })
    }

    scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score
        // Départage stable et lisible : ordre alphabétique du titre.
        return a.item.title.localeCompare(b.item.title)
    })

    return scored.slice(0, limit)
}

// ── Surlignage (gras accent-insensible aux bonnes positions) ─────────────────

export interface HighlightRange {
    start: number
    end: number
}

/**
 * Plages [start,end) à mettre en gras dans le TITRE ORIGINAL (positions
 * préservées par le repli 1:1). Surligne chaque token de la requête.
 */
export function highlightRanges(title: string, query: string): HighlightRange[] {
    const tokens = normalizeForSearch(query.trim())
        .split(/\s+/)
        .filter((t) => t.length > 0)
    if (tokens.length === 0) return []

    const folded = foldAccents(title.toLowerCase())
    const raw: HighlightRange[] = []
    for (const tk of tokens) {
        let from = 0
        for (;;) {
            const idx = folded.indexOf(tk, from)
            if (idx === -1) break
            raw.push({ start: idx, end: idx + tk.length })
            from = idx + tk.length
        }
    }
    if (raw.length === 0) return []

    // Fusionne les plages qui se chevauchent / se touchent.
    raw.sort((a, b) => a.start - b.start)
    const merged: HighlightRange[] = [raw[0]]
    for (let i = 1; i < raw.length; i++) {
        const last = merged[merged.length - 1]
        if (raw[i].start <= last.end) {
            last.end = Math.max(last.end, raw[i].end)
        } else {
            merged.push(raw[i])
        }
    }
    return merged
}

/** Segments (texte + drapeau gras) prêts au rendu, à partir des plages. */
export function highlightSegments(
    title: string,
    query: string,
): Array<{ text: string; bold: boolean }> {
    const ranges = highlightRanges(title, query)
    if (ranges.length === 0) return [{ text: title, bold: false }]
    const segments: Array<{ text: string; bold: boolean }> = []
    let cursor = 0
    for (const { start, end } of ranges) {
        if (start > cursor) segments.push({ text: title.slice(cursor, start), bold: false })
        segments.push({ text: title.slice(start, end), bold: true })
        cursor = end
    }
    if (cursor < title.length) segments.push({ text: title.slice(cursor), bold: false })
    return segments
}
